import { useEffect, useMemo, type ReactNode } from 'react'
import { closestCenter, DndContext, KeyboardSensor, PointerSensor, type DragEndEvent, useSensor, useSensors } from '@dnd-kit/core'
import { CSS } from '@dnd-kit/utilities'
import { SortableContext, arrayMove, rectSortingStrategy, sortableKeyboardCoordinates, useSortable } from '@dnd-kit/sortable'
import { GripVertical, Trash2 } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import type { Page, WireArticle } from '../../api/types'
import { MyAssignmentsList } from './MyAssignmentsList'
import { NeedsAttentionCard } from './ActionNeededList'
import { FrontPageSummaryCard } from './FrontPageCard'
import { FrontPageReadinessWidget } from './FrontPageReadinessWidget'
import { RecentActivityFeed } from './RecentActivityFeed'
import { RecentActivityTrendWidget } from './RecentActivityTrendWidget'
import { ReaderImpactWidget } from './ReaderImpactWidget'
import { PublishingCadenceWidget } from './PublishingCadenceWidget'
import { PipelineBottleneckWidget } from './PipelineBottleneckWidget'
import { ContentMixWidget } from './ContentMixWidget'
import { QualityChurnWidget } from './QualityChurnWidget'
import { SearchDiscoverabilityWidget } from './SearchDiscoverabilityWidget'
import { TeamLoadWidget } from './TeamLoadWidget'
import { PipelineGauges } from './PipelineGauges'
import { ADMIN_WIDGET_DEFINITIONS, type AdminWidgetId, type AdminWidgetItem, type AdminWidgetWidth, useAdminWidgetLayout } from './adminWidgetLayout'

interface AdminWidgetBoardProps {
  articles: WireArticle[]
  myArticles: WireArticle[]
  actionNeededCount: number
  loading: boolean
  livePage: Page | undefined
  scheduledPage: Page | undefined
  liveLoading: boolean
  scheduledLoading: boolean
  userSub: string
  pickerOpen: boolean
  onPickerOpenChange: (open: boolean) => void
}

function widthLabel(width: AdminWidgetWidth) {
  return width === 2 ? '2 cols' : '1 col'
}

function WidgetCard({
  item,
  width,
  title,
  description,
  icon: Icon,
  onRemove,
  onToggleWidth,
  children,
}: {
  item: AdminWidgetItem
  width: AdminWidgetWidth
  title: string
  description: string
  icon: LucideIcon
  onRemove: (id: AdminWidgetId) => void
  onToggleWidth: (id: AdminWidgetId) => void
  children: ReactNode
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <article
      ref={setNodeRef}
      style={style}
      className={`min-w-0 ${width === 2 ? 'lg:col-span-2' : 'lg:col-span-1'} ${isDragging ? 'opacity-60' : ''}`}
    >
      <div className="overflow-hidden rounded-2xl border border-line bg-surface shadow-sm transition-shadow hover:shadow-md">
        <div className="flex items-start justify-between gap-3 border-b border-line px-4 py-3">
          <div className="flex min-w-0 items-start gap-2">
            <button
              type="button"
              aria-label={`Drag ${title}`}
              className="mt-0.5 rounded p-1 text-fg-secondary transition-colors hover:bg-fill hover:text-fg"
              {...attributes}
              {...listeners}
            >
              <GripVertical size={14} aria-hidden="true" />
            </button>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <Icon size={14} aria-hidden="true" className="text-fg-secondary" />
                <h2 className="truncate text-sm font-semibold text-fg">{title}</h2>
                <span className="rounded-full bg-fill px-2 py-0.5 text-[10px] font-semibold text-fg-secondary">
                  {widthLabel(width)}
                </span>
              </div>
              <p className="mt-0.5 text-[11px] text-fg-secondary">{description}</p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <button
              type="button"
              onClick={() => onToggleWidth(item.id)}
              className="rounded border border-line bg-canvas px-2 py-1 text-[10px] font-semibold text-fg-secondary transition-colors hover:bg-fill hover:text-fg"
            >
              {width === 2 ? 'Make narrow' : 'Make wide'}
            </button>
            <button
              type="button"
              onClick={() => onRemove(item.id)}
              aria-label={`Remove ${title}`}
              className="rounded border border-line bg-canvas p-1.5 text-fg-secondary transition-colors hover:border-error-line hover:bg-error-surface hover:text-error-fg"
            >
              <Trash2 size={13} aria-hidden="true" />
            </button>
          </div>
        </div>
        <div>
          {children}
        </div>
      </div>
    </article>
  )
}

function AddWidgetPanel({
  availableWidgetIds,
  onAdd,
  onClose,
}: {
  availableWidgetIds: AdminWidgetId[]
  onAdd: (id: AdminWidgetId) => void
  onClose: () => void
}) {
  return (
    <div className="absolute right-0 top-12 z-20 w-[min(44rem,calc(100vw-2rem))] rounded-2xl border border-line bg-surface p-4 shadow-xl">
      <div className="mb-3 flex items-center justify-between">
        <div>
          <p className="text-sm font-semibold text-fg">Add Widgets</p>
          <p className="text-[11px] text-fg-secondary">Restore widgets you removed.</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded border border-line bg-canvas px-2 py-1 text-[10px] font-semibold text-fg-secondary transition-colors hover:bg-fill"
        >
          Close
        </button>
      </div>

      {availableWidgetIds.length === 0 ? (
        <div className="rounded-xl bg-fill/60 px-3 py-4 text-center text-xs text-fg-secondary">
          All widgets are already on the board.
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {availableWidgetIds.map((id) => {
            const widget = ADMIN_WIDGET_DEFINITIONS[id]
            const Icon = widget.icon
            return (
              <button
                key={id}
                type="button"
                onClick={() => onAdd(id)}
                className="flex w-full items-start justify-between gap-3 rounded-xl border border-line bg-canvas px-3 py-3 text-left transition-colors hover:bg-fill"
              >
                <div className="flex min-w-0 items-start gap-2">
                  <Icon size={14} aria-hidden="true" className="mt-0.5 shrink-0 text-fg-secondary" />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-fg">{widget.title}</p>
                    <p className="mt-0.5 text-[11px] text-fg-secondary">{widget.description}</p>
                  </div>
                </div>
                <span className="shrink-0 rounded-full bg-fill px-2 py-0.5 text-[10px] font-semibold text-fg-secondary">
                  {widget.defaultWidth === 2 ? '2 cols' : '1 col'}
                </span>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

export function AdminWidgetBoard({
  articles,
  myArticles,
  actionNeededCount,
  loading,
  livePage,
  scheduledPage,
  liveLoading,
  scheduledLoading,
  userSub,
  pickerOpen,
  onPickerOpenChange,
}: AdminWidgetBoardProps) {
  const { items, setItems, visibleIds, addWidget, removeWidget, setWidgetWidth, resetLayout } = useAdminWidgetLayout()

  useEffect(() => {
    function handleResetLayout() {
      resetLayout()
    }

    window.addEventListener('wire-ui-admin-reset-layout', handleResetLayout as EventListener)
    return () => window.removeEventListener('wire-ui-admin-reset-layout', handleResetLayout as EventListener)
  }, [resetLayout])

  const hiddenWidgets = useMemo(
    () => (Object.keys(ADMIN_WIDGET_DEFINITIONS) as AdminWidgetId[])
      .filter(id => !visibleIds.has(id))
      .sort((a, b) => ADMIN_WIDGET_DEFINITIONS[a].title.localeCompare(ADMIN_WIDGET_DEFINITIONS[b].title)),
    [visibleIds],
  )

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (!over || active.id === over.id) return

    setItems((current) => {
      const oldIndex = current.findIndex(item => item.id === active.id)
      const newIndex = current.findIndex(item => item.id === over.id)
      if (oldIndex < 0 || newIndex < 0) return current
      return arrayMove(current, oldIndex, newIndex)
    })
  }

  function toggleWidth(id: AdminWidgetId) {
    const item = items.find(entry => entry.id === id)
    if (!item) return
    setWidgetWidth(id, item.width === 2 ? 1 : 2)
  }

  function renderWidget(item: AdminWidgetItem) {
    const commonProps = {
      embedded: true,
    }

    switch (item.id) {
      case 'pipeline':
        return <PipelineGauges {...commonProps} articles={articles} myArticles={myArticles} actionNeededCount={actionNeededCount} loading={loading} />
      case 'myArticles':
        return <MyAssignmentsList {...commonProps} articles={myArticles} userSub={userSub} loading={loading} />
      case 'needsAttention':
        return <NeedsAttentionCard {...commonProps} articles={articles} loading={loading} />
      case 'frontPages':
        return <FrontPageSummaryCard {...commonProps} livePage={livePage} scheduledPage={scheduledPage} liveLoading={liveLoading} scheduledLoading={scheduledLoading} />
      case 'recentActivity':
        return <RecentActivityFeed {...commonProps} articles={articles} />
      case 'teamLoad':
        return <TeamLoadWidget {...commonProps} articles={articles} />
      case 'draftDistribution':
        return <PipelineBottleneckWidget {...commonProps} articles={articles} />
      case 'frontPageReadiness':
        return <FrontPageReadinessWidget {...commonProps} livePage={livePage} scheduledPage={scheduledPage} liveLoading={liveLoading} scheduledLoading={scheduledLoading} />
      case 'recentActivityTrend':
        return <RecentActivityTrendWidget {...commonProps} articles={articles} />
      case 'readerImpact':
        return <ReaderImpactWidget {...commonProps} articles={articles} />
      case 'publishingCadence':
        return <PublishingCadenceWidget {...commonProps} articles={articles} />
      case 'contentMix':
        return <ContentMixWidget {...commonProps} articles={articles} />
      case 'qualityChurn':
        return <QualityChurnWidget {...commonProps} articles={articles} />
      case 'searchSignals':
        return <SearchDiscoverabilityWidget {...commonProps} />
      default:
        return null
    }
  }

  return (
    <section className="px-8 pb-10">
      <div className="relative mb-4 flex items-center justify-end gap-3">
        {pickerOpen && (
          <AddWidgetPanel
            availableWidgetIds={hiddenWidgets}
            onAdd={(id) => {
              addWidget(id)
              onPickerOpenChange(false)
            }}
            onClose={() => onPickerOpenChange(false)}
          />
        )}
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={items.map(item => item.id)} strategy={rectSortingStrategy}>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {items.map((item) => (
              <WidgetCard
                key={item.id}
                item={item}
                width={item.width}
                title={ADMIN_WIDGET_DEFINITIONS[item.id].title}
                description={ADMIN_WIDGET_DEFINITIONS[item.id].description}
                icon={ADMIN_WIDGET_DEFINITIONS[item.id].icon}
                onRemove={removeWidget}
                onToggleWidth={toggleWidth}
              >
                {renderWidget(item)}
              </WidgetCard>
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {items.length === 0 && (
        <div className="mt-6 rounded-2xl border border-dashed border-line bg-surface px-6 py-10 text-center">
          <p className="text-sm font-semibold text-fg">No widgets on the board.</p>
          <p className="mt-1 text-xs text-fg-secondary">Use Add Widgets to restore the dashboard.</p>
        </div>
      )}
    </section>
  )
}
